/* eslint-disable eqeqeq */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useStylesheets } from 'hooks/useStylesheets';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { usePopupKeys } from 'hooks/usePopupKeys';
import { getBootUserId } from 'hooks/useDashboardData';
import { formatLegacyNumber } from 'pages/ahu-board/forms/formUtils';
import GlobalHeader from 'pages/dashboard/components/GlobalHeader';
import SizerFooter from './components/SizerFooter';
import MoreNotesPopup from './components/MoreNotesPopup';
import SizerSettingsPopup from './components/SizerSettingsPopup';
import SizerMatrix, { type SizerMatrixHandle } from './components/SizerMatrix';
import { getSizerSelectionController } from './selection/sizerSelectionHost';
import type { SizerPopupSelection } from './sizerStorage';
import { useSizerBackdropClick } from './useSizerBackdropClick';
import { useSizerSettings } from './useSizerSettings';

/**
 * Sizer CSS — the exact legacy cascade of `templates/sizer/styles.html:1-4`,
 * in order, ONE `useStylesheets` call:
 *   header.css → sizer/styles.css → common.css → bootstrap 3.3.4
 * `react.html` already ships common.css + header.css globally; re-injecting all
 * four here (appended after the globals) reproduces the last-occurrence order.
 *
 * ★ Two corrections to the discovery's §11 list:
 *  - BOOTSTRAP IS REQUIRED. `styles.html:4` loads it (via a relative href that
 *    resolves to `/static/vendor/...` from `/sizer/`), and the sizer markup is
 *    pure Bootstrap grid (`container-fluid`, `row`, `col-xs-*`, `col-md-6`)
 *    while `sizer/styles.css` defines ZERO `col-xs-12` rules and `common.css`
 *    defines no `col-md-*` at all. Without it the page has no layout.
 *    Route-scoped by `useStylesheets`, exactly like the project board scopes
 *    its bundled copy.
 *  - `css/sizer/skeleton.css` exists on disk but legacy never loads it — NOT
 *    added.
 */
const SIZER_CSS = [
  '/static/css/common/header.css',
  '/static/css/sizer/styles.css',
  '/static/css/common/common.css',
  '/static/vendor/bootstrap-3.3.4-dist/css/bootstrap.min.css',
];

/** `window.bot_ahu` — the chatbot hand-off (discovery §8f). */
function readBotAhu(): unknown {
  return (window as unknown as { bot_ahu?: unknown }).bot_ahu;
}

/**
 * AHU Sizer (`/sizer/` — Backbone app `sizer`), sub-phases 4a + 4b.
 *
 * 4a — the page shell: `tmpl/sizer.tmpl` (53 lines) rendered 1:1 — title strip,
 * the airflow input + its `airflow_hide` display twin + the Size button, the
 * `#matrix` / `#sizerSelection` / `#notes` / `#settings` mounts, and the footer
 * note strip with the gear and the "more notes…" trigger. Shared `GlobalHeader`
 * (`appName='sizer'` → empty `#ksdm-header` class, no workflow button) and the
 * sizerBase footer.
 *
 * 4b — the data layer (`app/services/sizerApi.ts`) and the settings popup
 * (`components/SizerSettingsPopup.tsx` + `useSizerSettings.ts`), including the
 * three `localStorage` contracts in `sizerStorage.ts`.
 *
 * ★ BLANK UNTIL LOADED — deliberate. The Backbone view's `render()` lives
 * inside the load `Promise.all(...).then` (main.js:134-141) and its `el` is
 * `#ksdm-content`, which the server ships EMPTY (`sizer.html` never overrides
 * `{% block content %}`). So the real page shows header + footer over an empty
 * content area until every collection resolves; the gate below reproduces that
 * rather than painting a half-built strip.
 *
 * ★ NOT PORTED (discovery §8a / §10):
 *  - the `#test` hash route (`controller/router.js:24-26` → `$('body').hide()`),
 *    a live debug leftover — dropped by decision.
 *  - `#sizerSelection` stays an EMPTY div: nothing ever populates it; its only
 *    reference is the `click .sizerSelection → eventStop` binding, which is
 *    really aimed at the header popup's root class (main.js:56).
 *  - the `workflow_popup/` tree — `#workflow_popup_button` does not exist on
 *    `/sizer/` (OBSERVED), so its handler can never fire.
 *
 * 4c — the matrix itself (`components/SizerMatrix.tsx` + `matrixEngine.ts` +
 * `matrixMath.ts`), mounted into `#matrix`, driven by
 * `settings.matrixRequest.revision`.
 *
 * 4d — sizing/optimals/hover/page behaviours: `changeAirflow`'s scroll reset +
 * `updateOptimals` (below), the blanket Esc/Enter handler
 * (`usePopupKeys({ blanket: true })`) and the backdrop-click branch
 * (`useSizerBackdropClick`).
 *
 * ★ SEAM STILL OPEN — 4e, the selection popup a matrix-cell click opens. It is
 * a HEADER feature (`django/static/mvc/header/view/sizer_popup.js`) with no
 * React counterpart, and it owns the only server WRITE in the whole sizer
 * feature. The seam is documented at its exact call site — `handleClick` in
 * `components/SizerMatrix.tsx` (main.js:596-637).
 */
const SizerPage = () => {
  useDocumentTitle('Sizer'); // sizer.html `{% block title %} Sizer {% endblock %}`
  // Gate first paint on the route CSS. These sheets are injected AFTER React
  // mounts and `react.html` only ships common.css + header.css, so without the
  // gate the header popups and the whole sizer strip paint as raw unstyled text
  // in the load gap (the FOUC class fixed twice already — see the ProjectBoard
  // and Dashboard `cssLoaded` gates). Legacy loads all four render-blocking in
  // <head>; holding a brief blank frame mirrors that.
  const cssLoaded = useStylesheets(SIZER_CSS);

  /**
   * ★ The AHU SELECTION POPUP's controller — a faithful port of the header's
   * `sizer_popup` Backbone view, and the SPA-WIDE SINGLETON that `GlobalHeader`
   * renders into `#sizer_popup` on every board (`selection/sizerSelectionHost.ts`,
   * `header.js:203-215`). This page is one of its HOSTS, not its owner: the only
   * thing on `/sizer/` that talks to it is the matrix cell click
   * (`main.js:598`, `:635-636`).
   *
   * 4e constructed it here and `destroy()`ed it on unmount, which is what
   * confined the popup to `/sizer/`. It is now created once per app instance and
   * never torn down, so an OPENED or MINIMIZED popup survives an SPA navigation
   * the way it survives a legacy full-page load.
   */
  const selection = getSizerSelectionController();

  // main.js:150-160 / :161-167 — the two blanket body handlers. `blanket: true`
  // is the sizer's opt-in to legacy's "dismiss EVERY active popup, the header
  // Ratings popup included" semantics (see `hooks/usePopupKeys.ts`).
  usePopupKeys({ blanket: true });
  useSizerBackdropClick({
    // The selection popup's backdrop only strips `.active`; its state machine
    // stays at `opened` and the next cell click brings it back (see the hook).
    onSelectionPopupBackdrop: () => selection.dismissByBackdrop(),
  });

  /** `openSelection`'s tail (main.js:635-636) — `update()` then `open_popup()`. */
  const handleSelectCell = useCallback(
    (data: SizerPopupSelection) => {
      selection.update(data);
      void selection.openPopup();
    },
    [selection]
  );

  // ---- airflow (sizer.tmpl:14-16) ----------------------------------------
  // Uncontrolled + a native `change` listener, for the same reason the settings
  // inputs are (see CachedInput): legacy binds the DOM `change` event, which
  // fires on blur/Enter — NOT per keystroke.
  const airflowRef = useRef<HTMLInputElement | null>(null);
  const airflowTwinRef = useRef<HTMLInputElement | null>(null);
  /** `.hide` swaps between the real input and its display twin. */
  const [twinVisible, setTwinVisible] = useState(false);
  /** `.non-clickable` on the Size button — `{{#unless airflow}}` at :16, then
   * `inputAirflow`/`changeAirflow` (main.js:644-660). */
  const [sizeDisabled, setSizeDisabled] = useState(true);

  const getAirflow = useCallback(() => airflowRef.current?.value ?? '', []);

  // ---- settings popup ----------------------------------------------------
  const [settingsActive, setSettingsActive] = useState(false);
  /** `openMorePopUp` APPENDS a new node on every click (main.js:748) and
   * `destroyNotes` removes ALL of them (`$('.notes').remove()`, :700-702). */
  const [noteCount, setNoteCount] = useState(0);

  /**
   * `render()`'s chatbot seed (main.js:285-287): `window.bot_ahu` overwrites the
   * airflow field on EVERY render — the initial one and each `render(true)`.
   * ⚠ It does NOT touch the Size button's `.non-clickable`, so the button stays
   * disabled until a real `input` event: reproduced (no `setSizeDisabled` here).
   * The 700 ms `window.bot_ahu` poll that AUTO-SIZES lives in MatrixView
   * (matrix.js:83-86) and lands with 4c.
   */
  const seedAirflowFromBot = useCallback(() => {
    const bot = readBotAhu();
    if (bot && airflowRef.current) airflowRef.current.value = String(bot);
  }, []);

  const onProductClassRerendered = useCallback(() => {
    // `render(true)` rebuilds the whole strip from the template, so the twin
    // goes back to hidden-and-empty and the real input shows its live value
    // (sizer.tmpl:14-15 — `airflow_hide` always renders `.hide` and blank).
    setTwinVisible(false);
    if (airflowTwinRef.current) airflowTwinRef.current.value = '';
    seedAirflowFromBot();
    // getDataDependsOnConstructionClass:417 — the popup is re-opened…
    setSettingsActive(true);
    // …and 300 ms later `.click_to_size` is auto-clicked (main.js:276-281).
    // jQuery's programmatic `.click()` ignores `pointer-events`, so it fires
    // even with an empty airflow — where `changeInput` then early-returns.
    // The click's `changeInput` leg is handled by the airflow change path
    // below; its `changeAirflow` leg (updateOptimals) lands with 4d.
    window.setTimeout(() => {
      airflowRef.current?.dispatchEvent(new Event('change', { bubbles: true }));
    }, 300);
  }, [seedAirflowFromBot]);

  const settings = useSizerSettings({ getAirflow, onProductClassRerendered });
  const { ready, requestMatrixUpdate } = settings;

  /** `self.matrixView` (main.js:327) — the handle `changeAirflow` drives. */
  const matrixRef = useRef<SizerMatrixHandle | null>(null);

  // ---- legacy side effects ------------------------------------------------
  // body[data-user_id] (sizerBase.html:12) — `react.html` has no such attribute.
  useEffect(() => {
    const id = getBootUserId();
    if (id != null) document.body.setAttribute('data-user_id', String(id));
    return () => {
      document.body.removeAttribute('data-user_id');
    };
  }, []);

  // Height chain: `sizer/styles.css:118-125,160-165` sizes `#ksdm-content` off
  // the html/body 100% chain; `#react-root` sits in between and must not break
  // it (same repair as the reports and AHU boards).
  useEffect(() => {
    const root = document.getElementById('react-root');
    const prev = root?.style.height ?? '';
    if (root) root.style.height = '100%';
    return () => {
      if (root) root.style.height = prev;
    };
  }, []);

  /**
   * ★ BUGFIX (4d). `cssLoaded` and `ready` are INDEPENDENT races — the sheets
   * are fetched in an effect, the collections over ~13 XHRs — and the airflow
   * strip only exists once BOTH are true (`if (!cssLoaded) return null` +
   * `{ready && …}`). Two effects below key off `ready` alone and grab
   * `airflowRef.current`; when CSS resolves LAST they run against a null ref and
   * never re-run, leaving the airflow field with no `change` listener at all —
   * i.e. Size does nothing and the matrix never re-POSTs.
   *
   * REPRODUCED on the Vite dev server (245 KB `sizer/styles.css` losing to the
   * API): zero POSTs and zero `.activeBorder` after typing 20 000; the
   * instrumented `addEventListener` log was EMPTY. Gating both on this single
   * "the strip is actually in the DOM" flag fixes it.
   */
  const airflowStripMounted = cssLoaded && ready;

  // The initial `render()`'s chatbot seed (the `render(true)` one runs from
  // `onProductClassRerendered`).
  const seededBotAhu = useRef(false);
  useLayoutEffect(() => {
    if (!airflowStripMounted || seededBotAhu.current) return;
    seededBotAhu.current = true;
    seedAirflowFromBot();
  }, [airflowStripMounted, seedAirflowFromBot]);

  // ---- airflow handlers ---------------------------------------------------
  /**
   * The DOM `change` on `input[name=airflow]` runs TWO delegated Backbone
   * handlers, in `events`-hash binding order (main.js:38 then :40):
   *   1. `changeInput` (:376-401) — early-returns on an EMPTY value, otherwise
   *      `updateMatrixData()`.
   *   2. `changeAirflow` (:638-652) — empty ⇒ disable Size and stop; else reset
   *      the matrix scroll to 0/0, `matrixView.updateOptimals(+airflow)` and
   *      enable Size.
   * Leg 2's matrix work is 4d; the enable/disable half is here so the button
   * behaves correctly today.
   */
  const handleAirflowChange = useCallback(() => {
    const raw = airflowRef.current?.value ?? '';
    if (raw) requestMatrixUpdate(); // leg 1
    // leg 2 — `.replace(',', '')` strips only the FIRST comma (legacy quirk).
    const airflow = raw.replace(',', '');
    if (!airflow) {
      setSizeDisabled(true);
      return;
    }
    // main.js:648-650 — reset the matrix scroll to 0/0 and run the optimal
    // (the SECOND POST call site: same endpoint, plus `optimal`). Queued rather
    // than run inline so it lands AFTER leg 1's `update_data`, exactly as it
    // does in legacy — see `SizerMatrixHandle.sizeTo`.
    matrixRef.current?.sizeTo(Number(airflow));
    setSizeDisabled(false); // :651
  }, [requestMatrixUpdate]);

  const handleAirflowChangeRef = useRef(handleAirflowChange);
  handleAirflowChangeRef.current = handleAirflowChange;

  useEffect(() => {
    const el = airflowRef.current;
    if (!el) return undefined;
    const handler = () => handleAirflowChangeRef.current();
    el.addEventListener('change', handler);
    return () => el.removeEventListener('change', handler);
  }, [airflowStripMounted]);

  /** `inputAirflow` (main.js:653-661) — bound to the `input` event. */
  const handleAirflowInput = useCallback(() => {
    const airflow = (airflowRef.current?.value ?? '').replace(',', '');
    setSizeDisabled(!airflow);
  }, []);

  /** `onEnter` (main.js:370-372) — Enter re-fires `change` on the field. */
  const handleAirflowKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.keyCode === 13) {
      e.currentTarget.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }, []);

  /**
   * `onBlur` (main.js:662-667): hide the real input, show the twin, MIRROR the
   * raw value into it and run `validateNumberField` on the TWIN ONLY — so the
   * twin shows "20,000" while the real input keeps "20000". The twin declares
   * no `data-min`/`data-max`/`data-step`, so validation is just
   * `Math.round` + the comma formatter, i.e. `formatLegacyNumber(v)` (already a
   * bug-for-bug port of :337-369 — `formUtils.formatLegacyNumber`).
   */
  const handleAirflowBlur = useCallback(() => {
    const raw = airflowRef.current?.value ?? '';
    setTwinVisible(true);
    const twin = airflowTwinRef.current;
    if (twin) twin.value = raw === '' ? '' : formatLegacyNumber(raw);
  }, []);

  /** `onFocus` (main.js:668-672) — the twin hands focus back to the real input. */
  const handleTwinFocus = useCallback(() => {
    setTwinVisible(false);
    window.setTimeout(() => airflowRef.current?.focus(), 0);
  }, []);

  /** `forceSize` (main.js:373-375) — `$('input[name=airflow]').change()`. */
  const handleSizeClick = useCallback(() => {
    if (sizeDisabled) return; // `.non-clickable` is pointer-events:none in CSS
    airflowRef.current?.dispatchEvent(new Event('change', { bubbles: true }));
  }, [sizeDisabled]);

  /** `openSizerSettingsPopUp` (main.js:752-758). */
  const openSettings = useCallback(() => {
    setSettingsActive(true);
    window.setTimeout(() => settings.bumpEllipsis(), 100);
  }, [settings]);

  // Hold a blank frame until the route stylesheets have applied.
  if (!cssLoaded) return null;

  return (
    <>
      {/* The selection popup is rendered by the header itself now, into
          `#sizer_popup` — see `selection/sizerSelectionHost.ts`. */}
      <GlobalHeader appName="sizer" />

      {/* ★ HEIGHT-CHAIN REPAIR (4c). `sizer/styles.css:160-165` gives
          `#ksdm-content { height: 100% }`, and the whole matrix sizes off it:
          `.matrix-height` calc(100% - 40px) → `.scrollbar-table` →
          `#main-table-container`, which is what the windowing math measures.
          The bundled dashboard CSS leaks `:global(body #ksdm-content){height:
          calc(100vh - 53px)}` (`pages/dashboard/components/StatusesRail.module.scss:22`),
          a higher-specificity rule that wins on EVERY React route — measured
          here as `#ksdm-content` 897px vs legacy's 950px @1600×950, i.e. a
          53px-shorter grid (260 blocks instead of 280). Carrying the legacy
          value as an inline style out-ranks the leak without touching the
          dashboard sheet — the same route-scoped repair the AHU board already
          uses (`pages/ahu-board/AhuBoardPage.tsx:185-199, 439`). */}
      <div id="ksdm-content" style={{ height: '100%' }}>
        {ready && (
          <div className="container-fluid sizer">
            <div className="row sizer_item">
              <div className="height col-xs-12">
                <div className="height">
                  <div className="height col-xs-12">
                    {/* ---- title strip (sizer.tmpl:6-18) ---- */}
                    <div className="col-xs-12 sizer-header">
                      <div>
                        <h3 className="text-uppercase sizer-header__title">Sizer</h3>{' '}
                        <span className="text-lowercase">air handling unit</span>
                      </div>
                      <div className="sizer-container__items">
                        <span className="label">Airflow</span>{' '}
                        <span className="unit unit-text">CFM</span>{' '}
                        <input
                          ref={airflowRef}
                          type="number"
                          name="airflow"
                          className={`header-input${twinVisible ? ' hide' : ''}`}
                          autoComplete="off"
                          onChange={handleAirflowInput}
                          onKeyDown={handleAirflowKeyDown}
                          onBlur={handleAirflowBlur}
                        />{' '}
                        <input
                          ref={airflowTwinRef}
                          data-type="number"
                          type="text"
                          name="airflow_hide"
                          className={`header-input integer${twinVisible ? '' : ' hide'}`}
                          autoComplete="off"
                          onFocus={handleTwinFocus}
                        />{' '}
                        <button
                          type="button"
                          className={`click_to_size btn${sizeDisabled ? ' non-clickable' : ''}`}
                          onClick={handleSizeClick}
                        >
                          Size
                        </button>
                      </div>
                    </div>

                    {/* ---- body (sizer.tmpl:19-49) ---- */}
                    <div className="col-xs-12 sizer-body">
                      <div className="row height">
                        <div className="col-xs-12 height">
                          <div className="col-xs-12 sizer-container height">
                            <div className="col-xs-12 height">
                              <div className="row height">
                                <div className="col-xs-12 sizer-container__items height">
                                  {/* sizer.tmpl:27-29 — `#matrix` is MatrixView's
                                      `el`; `render_base_element` replaces its
                                      contents with `tmpl/matrix.tmpl`
                                      (matrix.js:25, :228-233).
                                      ★ KEYED ON THE CONSTRUCTION CLASS so a
                                      Product Class change unmounts and rebuilds
                                      the whole matrix — which is exactly what
                                      legacy `render(true)` does by constructing
                                      a brand-new MatrixView (main.js:327-331).
                                      While the new class's dimensions are in
                                      flight the matrix is absent, mirroring
                                      legacy's wholesale `$el.html(...)` wipe. */}
                                  <div className="col-xs-12 matrix-height">
                                    <div className="row height" id="matrix">
                                      {settings.cabinetDimensions &&
                                        settings.pagesCount &&
                                        settings.pageSize && (
                                          <SizerMatrix
                                            key={settings.cabinetDimensions.construction_class_id}
                                            ref={matrixRef}
                                            cabinetDimensions={settings.cabinetDimensions}
                                            pagesCount={settings.pagesCount}
                                            pageSize={settings.pageSize}
                                            deviation={settings.deviation ?? null}
                                            optimalSizes={settings.optimalSizes ?? []}
                                            matrixRequest={settings.matrixRequest}
                                            onSelectCell={handleSelectCell}
                                          />
                                        )}
                                    </div>
                                  </div>
                                  {/* Never populated — dead (:30, discovery §10). */}
                                  <div id="sizerSelection" className="" />
                                  <div id="notes" className="">
                                    {Array.from({ length: noteCount }, (_, i) => (
                                      <MoreNotesPopup key={i} onClose={() => setNoteCount(0)} />
                                    ))}
                                  </div>
                                  <div id="settings" className="">
                                    <SizerSettingsPopup
                                      settings={settings}
                                      active={settingsActive}
                                      onClose={() => setSettingsActive(false)}
                                    />
                                  </div>
                                  {/* ---- footer notes (sizer.tmpl:33-42) ---- */}
                                  <div className="col-xs-12 collapse-content__footer">
                                    <div className="row">
                                      <span className="collapse-content__footer-items">
                                        1. CW and CH are Cabinet Outside Dimensions.
                                      </span>{' '}
                                      <span className="collapse-content__footer-items">
                                        2. CH Cabinet Height does not include height of unit base,
                                        roof seam and roof slope (when applicable).
                                      </span>{' '}
                                      <span
                                        id="sizer_settings"
                                        className="fa-cog icon-settings-1 pointer vertical-center collapseIcon right"
                                        onClick={openSettings}
                                      />{' '}
                                      <span
                                        className="collapse-content__footer-items-notes pointer right"
                                        id="moreNotes"
                                        onClick={() => setNoteCount((n) => n + 1)}
                                      >
                                        {' '}
                                        more notes...{' '}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <SizerFooter />
    </>
  );
};

export default SizerPage;
